import {json, texture} from "p3";
import common from "../common";
import definitions from "../definitions";
import CoinAnimation from "../game/actor/misc/CoinAnimation";
import MinigameChickenRun from "../game/quest/minigames/MinigameChickenRun";
import MinigameCrabCatch from "../game/quest/minigames/MinigameCrabCatch";
import MinigameDucklingTrain from "../game/quest/minigames/MinigameDucklingTrain";
import MinigameSimonSays from "../game/quest/minigames/MinigameSimonSays";
import MinigameSnakeAttack from "../game/quest/minigames/MinigameSnakeAttack";
import MinigameSwanlingTrain from "../game/quest/minigames/MinigameSwanlingTrain";
import CoinCounterPanel from "../ui/CoinCounterPanel";
import IdleMessage from "../ui/idlemessage/IdleMessage";
import {iswebfont, locale, resizeLabelToFitText} from "../utils";
import GameScene from "./GameScene";
import MenuScene from "./MenuScene";

export default class MinigameResultScene extends MenuScene {
    /**
     * @param {Quest} minigame
     */
    constructor(minigame) {
        super("minigame_result");

        this._minigame = minigame;

        this._panel = null;
        this._title = null;
        this._closeButton = null;
        this._score = null;
        this._coinHolder = null;
    }

    onSceneLoaded() {
        super.onSceneLoaded();

        this.interactive = true;

        this.createFill();

        this._panel = new PIXI.Container();
        this._panel.y = 200;
        this.addChild(this._panel);

        let tile = new PIXI.Sprite(texture("quest_panel_tile"));
        tile.anchor.set(0.5, 0);
        this._panel.addChild(tile);

        tile = new PIXI.Sprite(texture("quest_panel_tile"));
        tile.y = tile.height;
        tile.anchor.set(0.5, 1);
        this._panel.addChild(tile);
        tile.rotation = Math.PI;

        this._panel.header = new PIXI.Sprite(texture("quest_panel_edge"));
        this._panel.header.position.set(0, 10);
        this._panel.header.anchor.set(0.5);
        this._panel.addChild(this._panel.header);

        this._panel.footer = new PIXI.Sprite(texture("quest_panel_edge"));
        this._panel.footer.position.set(0, this._panel.height - 20);
        this._panel.footer.anchor.set(0.5);
        this._panel.addChild(this._panel.footer);

        const deco1 = new PIXI.Sprite(texture("plant_top_left"));
        deco1.position.set(-308, -54);
        this._panel.addChild(deco1);

        const deco2 = new PIXI.Sprite(texture("plant_top_right"));
        deco2.position.set(306, -40);
        deco2.anchor.set(1, 0);
        this._panel.addChild(deco2);

        const deco3 = new PIXI.Sprite(texture("plant_bottom_left"));
        deco3.position.set(-306, this._panel.height - 100);
        deco3.anchor.set(0, 1);
        this._panel.addChild(deco3);

        const deco4 = new PIXI.Sprite(texture("plant_bottom_right"));
        deco4.position.set(306, this._panel.height - 100);
        deco4.anchor.set(1, 1);
        this._panel.addChild(deco4);

        this._title = new PIXI.NineSlicePlane(texture("panel_quest_completed"), 30, 0, 30, 0);
        this._title.position.set(0, -12);
        this._title.pivot.set(0, this._title.texture.height * 0.5);
        this._panel.addChild(this._title);

        if (!iswebfont()) {
            this._title.label = new PIXI.BitmapText(locale('ui_minigame_' + (this._minigame.score > 0 ? 'complete' : 'fail') + '_title').toUpperCase(), {
                fontName: "Alghera Pro",
                fontSize: 32,
                align: "center",
                tint: 0x0
            });
        } else {
            this._title.label = new PIXI.Text(locale('ui_minigame_' + (this._minigame.score > 0 ? 'complete' : 'fail') + '_title').toUpperCase(), {
                fontFamily: "FredFredBurger",
                fontSize: 32,
                align: "center",
                fill: 0x0
            });
        }
        this._title.label.position.set(0, !iswebfont() ? 37 : 32);
        this._title.label.anchor.set(0.5);
        this._title.addChild(this._title.label);
        resizeLabelToFitText(this._title, 400);

        this._title.icon = new PIXI.Sprite(texture("icon_npc_quest_complete"));
        this._title.icon.y = -10;
        this._title.icon.anchor.set(0.5);
        this._title.icon.scale.set(1);
        this._title.addChild(this._title.icon);

        this._title.width = this._title.label.textWidth + 50;
        this._title.pivot.x = this._title.width * 0.5;
        this._title.label.x = this._title.width * 0.5;
        this._title.icon.x = this._title.width * 0.5;

        this._avatarLeft = new PIXI.Sprite(texture("dialogue_icon_ivandoe"));
        this._avatarLeft.position.set(-194, 90);
        this._avatarLeft.anchor.set(0.5);
        this._panel.addChild(this._avatarLeft);

        this._avatarRight = new PIXI.Sprite(this.getAvatarTexture());
        this._avatarRight.position.set(194, 90);
        this._avatarRight.anchor.set(0.5);
        this._avatarRight.scale.x = -1;
        this._panel.addChild(this._avatarRight);

        if (!iswebfont()) {
            this._copyLabel = new PIXI.BitmapText(locale('ui_minigame_' + (this._minigame.score > 0 ? 'complete' : 'fail') + '_subtitle'), {
                fontName: "Alghera Pro",
                fontSize: 32,
                align: "center",
                tint: 0x938262,
                maxWidth: 320
            });
        } else {
            this._copyLabel = new PIXI.Text(locale('ui_minigame_' + (this._minigame.score > 0 ? 'complete' : 'fail') + '_subtitle'), {
                fontFamily: "FredFredBurger",
                fontSize: 32,
                align: "center",
                fill: 0x938262,
                wordWrap: true,
                wordWrapWidth: 320
            });
        }

        this._copyLabel.position.set(0, !iswebfont() ? 106 : 102);
        this._copyLabel.anchor.set(0.5);
        this._panel.addChild(this._copyLabel);

        this._score = new PIXI.Container();
        this._score.position.set(-150, 226);
        this._panel.addChild(this._score);

        this._score.frame = new PIXI.NineSlicePlane(texture("quest_side_frame_9slice"), 40, 40, 40, 40);
        this._score.frame.width = 220;
        this._score.frame.height = 100;
        this._score.frame.pivot.x = this._score.frame.width * 0.5;
        this._score.frame.pivot.y = this._score.frame.height * 0.5;
        this._score.addChild(this._score.frame);

        this._score.icon = new PIXI.Sprite(this.getIconTexture());
        this._score.icon.position.set(-50, 0);
        this._score.icon.anchor.set(0.5);
        this._score.addChild(this._score.icon);

        if (!iswebfont()) {
            this._score.valueLabel = new PIXI.BitmapText("x" + this._minigame.score, {
                fontName: "Alghera Pro",
                fontSize: 60,
                align: "center",
                tint: 0x732612
            });
        } else {
            this._score.valueLabel = new PIXI.Text("x" + this._minigame.score, {
                fontFamily: "FredFredBurger",
                fontSize: 60,
                align: "center",
                fill: 0x732612
            });
        }
        this._score.valueLabel.position.set(22, !iswebfont() ? 5 : -2);
        this._score.valueLabel.anchor.set(0.5);
        this._score.addChild(this._score.valueLabel);

        this._coinHolder = new CoinCounterPanel();
        this._coinHolder.x = 150;
        this._coinHolder.y = this._score.y;
        this._panel.addChild(this._coinHolder);
        this._coinHolder.coinAmount = this._minigame.coins * this._minigame.score;

        this._closeButton = P3.Button.create(texture("btn_action_medium_out"), null, null, texture("btn_icon_medium_tick"));
        this._closeButton.interactive = false;
        this._closeButton.position.set(0, this._panel.height - 140);
        this._closeButton.signals.click.add(this.animateHide, this);
        this._panel.addChild(this._closeButton);

        console.log(this._minigame);
        common.tracking.trackEvent(definitions.TRACKING.EVENT_TYPE.INTERACTION, {
            section: common.tracking.getSection(),
            contenttitle: json('locale')[this._minigame.name].en + "|score:" + this._minigame.score,
            interaction: "minigame completed",
            progress: common.tracking.progress,
            interactionevent: 1
        });

        this.resize();

        this.animateShow(() => {
            const oldCoins = common.saveData.activeSlot.coins;
            const coinsToAdd = this._minigame.coins * this._minigame.score;
            common.saveData.activeSlot.coins += coinsToAdd;
            common.saveData.saveGame();
            
            if (GameScene.active.hud.coinHolder && coinsToAdd > 0) {
                const startPos = this._coinHolder.toGlobal(new PIXI.Point(0, 0));
                startPos.x += (common.STAGE_WIDTH/2) - (P3.View.width/2);
                const endPos = GameScene.active.hud.coinHolder.toGlobal(new PIXI.Point(0, 0));
                endPos.x += (common.STAGE_WIDTH/2) - (P3.View.width/2);
                const coinEffect = new CoinAnimation(coinsToAdd, GameScene.active, (percent) => {
                    GameScene.active.hud.coinHolder.coinAmount = oldCoins + Math.round(percent * coinsToAdd);
                    if (Math.round(percent * coinsToAdd) == coinsToAdd) {
                        this._closeButton.interactive = true;
                    }
                }, {x:-(startPos.x - endPos.x), y:-(startPos.y - endPos.y)});
                coinEffect.x = startPos.x;
                coinEffect.y = startPos.y;
                this.addChild(coinEffect);
            } else {
                this._closeButton.interactive = true;
            }
        });
    }

    destroy() {
        this._closeButton.destroy();
        if (GameScene.active.hud.coinHolder) {
            GameScene.active.hud.coinHolder.coinAmount = common.saveData.activeSlot.coins;
        }

        super.destroy();
    }

    resize() {
        super.resize();

        if (this.isSceneLoaded) {
            this._panel.position.x = common.STAGE_WIDTH * 0.5;
        }
    }

    animateShow(callback) {
        
        GameScene.active.addTween(gsap.to(this._panel, 0.7, {y:this._panel.y, ease:Expo.easeOut, onComplete:() => {
            if (callback) {
                callback();
            }
        }}));
        this._panel.y = -common.STAGE_HEIGHT;
    }

    animateHide() {
        this._closeButton.interactive = false;
        GameScene.active.addTween(gsap.to(this._panel, 0.4, {y:-common.STAGE_HEIGHT, ease:Expo.easeIn, onComplete:() => {
            if(this._minigame.coins > 0 && common.ads.isAdEnabled(definitions.AD_PLACEMENTS.MINIGAME_BONUS)) {
                this.showCoinDoublePanel();
            } else {
                this.onCloseButtonClick();
            }
        }}));
    }

    onCloseButtonClick() {
        this.signals.next.dispatch(this);
    }

    /**
     * @return {PIXI.Texture}
     */
    getAvatarTexture() {
        try {
            const data = json(this._minigame.owner);
            return texture(data.character.avatar);
        } catch {
        }
        return PIXI.Texture.EMPTY;
    }

    showCoinDoublePanel() {

        const coinReward = this._minigame.coins * this._minigame.score;

        const panel = new PIXI.NineSlicePlane(texture("panel_popup_9slice"), 80, 110, 80, 80);
        panel.width = 520;
        panel.height = 310;
        panel.x = (common.STAGE_WIDTH/2) - (panel.width/2);
        panel.y = common.STAGE_HEIGHT;
        this.addChild(panel);

        const viewAdLabelHolder = new PIXI.NineSlicePlane(texture('ad_label'), 17, 7, 17, 7);
        panel.addChild(viewAdLabelHolder);

        let viewAdLabel;
        if (!iswebfont()) {
            viewAdLabel = new PIXI.BitmapText(locale('ui_watch_ad_description'), {
                fontName: "Alghera Pro",
                fontSize: 24,
                align: "left",
                tint: 0x000000
            });
        } else {
            viewAdLabel = new PIXI.Text(locale('ui_watch_ad_description'), {
                fontFamily: "FredFredBurger",
                fontSize: 22,
                align: "left",
                fill: 0x0
            });
        }
        viewAdLabel.anchor.set(0.5);
        viewAdLabelHolder.addChild(viewAdLabel);
        resizeLabelToFitText(viewAdLabel, (panel.width - 50) - viewAdLabelHolder.leftWidth - viewAdLabelHolder.rightWidth);

        viewAdLabelHolder.width = viewAdLabel.width + viewAdLabelHolder.leftWidth + viewAdLabelHolder.rightWidth;
        viewAdLabelHolder.position.set((panel.width/2) - (viewAdLabelHolder.width/2), -(viewAdLabelHolder.height) + 10);
        viewAdLabel.position.set(viewAdLabelHolder.width / 2, (viewAdLabelHolder.height/2) + 2);

        let title;
        if (!iswebfont()) {
            title = new PIXI.BitmapText(locale('ad_double_minigame_coins_title'), {
                fontName: "Alghera Pro",
                fontSize: 40,
                align: "center",
                tint: 0x000000
            });
        } else {
            title = new PIXI.Text(locale('ad_double_minigame_coins_title'), {
                fontFamily: "FredFredBurger",
                fontSize: 40,
                align: "center",
                fill: 0x0
            });
        }
        title.anchor.set(0.5);
        title.position.set(panel.width/2, !iswebfont() ? 60 : 57);
        panel.addChild(title);
        resizeLabelToFitText(title, panel.width-60);

        const imageHolder = new PIXI.Container();
        imageHolder.position.set(panel.width/2, 60);
        panel.addChild(imageHolder);

        const image = new PIXI.Sprite(texture("prop_ui_coin_stack_00"));
        image.anchor.set(0.5, 0);
        imageHolder.addChild(image);

        const multiplierHolder = new PIXI.Container();
        imageHolder.addChild(multiplierHolder);

        let currentX = 0;
        for(let i = 0; i < 2; i++) {
            let coinIcon = new PIXI.Sprite(texture("icon_coin"));
            coinIcon.x = currentX;
            coinIcon.anchor.set(0, 0.5);
            multiplierHolder.addChild(coinIcon);
            currentX += coinIcon.width + 3;

            let coinLabel;
            if (!iswebfont()) {
                coinLabel = new PIXI.BitmapText(String(coinReward), {
                    fontName: "Alghera Pro",
                    fontSize: 28,
                    align: "left",
                    tint: 0x000000
                });
            } else {
                coinLabel = new PIXI.Text(String(coinReward), {
                    fontFamily: "FredFredBurger",
                    fontSize: 28,
                    align: "left",
                    fill: 0x0
                });
            }
            coinLabel.anchor.set(0, 0.5);
            coinLabel.x = currentX;
            coinLabel.y = !iswebfont() ? 0 : -3;
            multiplierHolder.addChild(coinLabel);

            currentX += coinLabel.width;

            if (i == 0) {
                const plusSign = new PIXI.BitmapText("+", {
                    fontName: "Alghera Pro",
                    fontSize: 28,
                    align: "left",
                    tint: 0x000000
                });
                plusSign.anchor.set(0, 0.5);
                plusSign.x = currentX + 3;
                multiplierHolder.addChild(plusSign);

                currentX += plusSign.width + 6;
            }
        }

        multiplierHolder.position.set(-(multiplierHolder.width/2), image.height + 5);

        let description;
        if (!iswebfont()) {
            description = new PIXI.BitmapText(locale('ad_double_minigame_coins_description'), {
                fontName: "Alghera Pro",
                fontSize: 30,
                align: "center",
                tint: 0x702311,
                maxWidth: panel.width-60
            });
        } else {
            description = new PIXI.Text(locale('ad_double_minigame_coins_description'), {
                fontFamily: "FredFredBurger",
                fontSize: 30,
                align: "center",
                fill: 0x702311,
                wordWrap: true,
                wordWrapWidth: panel.width-60
            });
        }
        description.anchor.set(0.5, 0);
        description.position.set(panel.width/2, imageHolder.y + (imageHolder.height) + 20 + (!iswebfont() ? 0 : -3));
        panel.addChild(description);

        panel.height = Math.max(panel.height, description.y + description.height + panel.bottomHeight);

        let errorMessage;

        const hidePanel = () => {
            this._tweens.push(
                gsap.to(panel, 0.3, {y:common.STAGE_HEIGHT, ease:Expo.easeIn, onComplete:() => {
                    this.onCloseButtonClick();
                }})
            );
            if (errorMessage) {
                try{
                    this.removeChild(errorMessage);
                } catch(e){}
                errorMessage.destroy();
                errorMessage = null;
            }
        }

        const watchButton = P3.Button.create(texture("btn_rectangle_out"));
        watchButton.overSoundName = "sfx_btn_generic_rollover";
        watchButton.clickSoundName = "sfx_btn_generic_press_00";

        let nameLabel;
        if (!iswebfont()) {
            nameLabel = new PIXI.BitmapText(locale('ui_watch_ad').toUpperCase(), {
                fontName: "Alghera Pro Outline",
                fontSize: 32,
                align: "left",
                tint: 0xFFFFFF
            });
        } else {
            nameLabel = new PIXI.Text(locale('ui_watch_ad').toUpperCase(), {
                fontFamily: "FredFredBurger",
                fontSize: 32,
                align: "left",
                fill: 0xFFFFFF
            });
        }
        nameLabel.y = !iswebfont() ? 0 : -3;
        nameLabel.anchor.set(0.5);
        watchButton._background.addChild(nameLabel);
        resizeLabelToFitText(nameLabel, 280);

        watchButton.x = (panel.width/2);
        watchButton.y = panel.height;
        panel.addChild(watchButton);

        const closeButton = P3.Button.create(
            texture("btn_small_out"),
            texture("btn_small_out"),
            texture("btn_small_out"),
            texture("btn_icon_cross"),
        );
        closeButton.x = panel.width + 20;
        closeButton.y = 20;
        panel.addChild(closeButton);

        this.signalListeners.push(
            watchButton.signals.click.add(() => {

                common.ads.signals.viewed.detachAll();
                common.ads.signals.fail.detachAll();
                
                watchButton.interactive = closeButton.interactive = false;
                watchButton.alpha = closeButton.alpha = 0.5;

                const getReward = () => {
                    const oldCoins = common.saveData.activeSlot.coins;
                    common.saveData.activeSlot.coins += coinReward;
                    common.saveData.saveGame();
                    
                    if (GameScene.active.hud.coinHolder) {
                        const endPos = GameScene.active.hud.coinHolder.toGlobal(new PIXI.Point(0, 0));
                        endPos.x += (common.STAGE_WIDTH/2) - (P3.View.width/2);
                        const coinEffect = new CoinAnimation(coinReward, GameScene.active, (percent) => {
                            GameScene.active.hud.coinHolder.coinAmount = oldCoins + Math.round(percent * coinReward);
                        }, {x:-((common.STAGE_WIDTH / 2) - endPos.x), y:-((common.STAGE_HEIGHT / 2) - endPos.y)});
                        coinEffect.x = common.STAGE_WIDTH / 2;
                        coinEffect.y = common.STAGE_HEIGHT / 2;
                        GameScene.active.addChild(coinEffect);
                    }
                    hidePanel();
                }
                if (common.ads.showAd()) {
                    this.signalListeners.push(
                        common.ads.signals.viewed.once((rewarded) => {
                            if (rewarded) {
                                getReward();
                            } else {
                                watchButton.interactive = closeButton.interactive = true;
                                watchButton.alpha = closeButton.alpha = 1;
                            }
                        }),
                        common.ads.signals.fail.once(() => {
                            watchButton.interactive = closeButton.interactive = true;
                            watchButton.alpha = closeButton.alpha = 1;

                            if(errorMessage == null) {
                                errorMessage = new IdleMessage(locale("ad_fail_message"), () => {
                                    try{
                                        this.removeChild(errorMessage);
                                    } catch(e){}
                                    errorMessage.destroy();
                                    errorMessage = null;
                                });
                                errorMessage.x = (common.STAGE_WIDTH / 2) - (P3.View.width / 2);
                                errorMessage.y = 150;
                                this.addChild(errorMessage);
                            }
                        })
                    );
                } else {
                    getReward();
                }
            }, this)
        )
        
        this.signalListeners.push(
            closeButton.signals.click.add(() => {
                hidePanel();
            }, this)
        );

        this._tweens.push(
            gsap.to(panel, 0.3, {y:(common.STAGE_HEIGHT / 2) - (panel.height/2), ease:Expo.easeOut})
        );
    }

    /**
     * @return {PIXI.Texture}
     */
    getIconTexture() {
        if (this._minigame instanceof MinigameSwanlingTrain) {
            return texture("minigame_icon_swanling");
        }
        if (this._minigame instanceof MinigameCrabCatch) {
            return texture("minigame_icon_crab");
        }
        if (this._minigame instanceof MinigameChickenRun) {
            return texture("minigame_icon_chicken");
        }
        if (this._minigame instanceof MinigameDucklingTrain) {
            return texture("minigame_icon_duckling");
        }
        if (this._minigame instanceof MinigameSnakeAttack) {
            return texture("minigame_icon_snake");
        }
        if (this._minigame instanceof MinigameSimonSays) {
            return texture("prop_bell_puzzle_01");
        }
        return PIXI.Texture.EMPTY;
    }

    /**
     * @return {Quest}
     */
    get minigame() {
        return this._minigame;
    }
}

/**
 * @static
 * @type {GameScene}
 */
MinigameResultScene.active = null;